import { ALL_VALUE } from '../API/ItemsGetters'
import { AdminBackendPermissionNames, BackendPermissionNames } from '../Constants/Permissions'
import { PermissionsBuilder } from './PermissionsBuilderUtil'
import { Permissions } from '../Contexts/UserInfoContext'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { AdminUserResponse } from '../../../NonReact/common/Types/Types'

/**
 * The function `isAdmin` checks if the current user is an admin based on their scope and permissions.
 * @param {AdminUserResponse} currentUser - The `currentUser` parameter is of type `AdminUserResponse
 */
export const isAdmin = (currentUser: AdminUserResponse): boolean =>
    currentUser.Scope.Admin || currentUser.Permissions.Admin.length > 0

/**
 * The function isCMEWideUser checks if the current user's TelSysID includes a specific value.
 * @param {AdminUserResponse} currentUser - The `currentUser` parameter is of type `AdminUserResponse`,
 */
export const isCMEWideUser = (currentUser: AdminUserResponse): boolean => currentUser.Scope.TelSysID.includes(ALL_VALUE)

/**
 * The function isTelSysWideUser checks if the current user's TelSysID includes a specific value.
 * @param {AdminUserResponse} currentUser - The `currentUser` parameter is of type `AdminUserResponse`
 */
export const isTelSysWideUser = (currentUser: Definable<AdminUserResponse>): Definable<boolean> =>
    isVarNotEmpty(currentUser)
        ? currentUser.Scope.TelSysID.length > 0 && !currentUser.Scope.TelSysID.includes(ALL_VALUE)
        : undefined

export const hasTelSysType = (currentUser: AdminUserResponse, telSysType: TelSysTypes): boolean =>
    currentUser.Scope.TelSysTypes.includes(telSysType)

/**
 * The function `hasPermission` checks if a given user has a specific permission based on their role.
 * @param {AdminUserResponse} currentUser - The `currentUser` parameter is of type `AdminUserResponse`,
 * @param {string} permission - The `permission` parameter is a string. E.G "Report|Read"
 */
export const hasPermission = (currentUser: Definable<AdminUserResponse>, permission: string): boolean =>
    !!(
        currentUser?.Permissions?.Admin?.includes(permission) ||
        currentUser?.Permissions?.NonAdmin?.includes(permission) ||
        currentUser?.Permissions?.Other?.includes(permission)
    )

/**
 * The function `hasPermissions` checks if the current user has all the specified permissions.
 * @param {AdminUserResponse} currentUser - The `currentUser` parameter is of type `AdminUserResponse`,
 * @param {string[]} permissions - The `permissions` parameter is an array of strings that represent
 * @param {boolean} allowSome - The `allowSome` parameter is a boolean that determines if the user has some or all
 * the permissions
 */
export const hasPermissions = (currentUser: AdminUserResponse, permissions: string[], allowSome?: boolean): boolean =>
    allowSome
        ? permissions.some((permission: string): boolean => hasPermission(currentUser, permission))
        : permissions.every((permission: string): boolean => hasPermission(currentUser, permission))

/* The PermissionsUtil class provides methods to check if the current user is an admin, has permissions
 * and if they are a CME wide user.
 */
export class PermissionsUtil {
    static builder(fieldName: AdminBackendPermissionNames | BackendPermissionNames): PermissionsBuilder {
        return new PermissionsBuilder(fieldName)
    }

    static isAdmin(currentUser: Definable<AdminUserResponse>): boolean {
        if (!currentUser) return false
        return currentUser ? isAdmin(currentUser) : false
    }

    static hasPermissions(
        currentUser: Definable<AdminUserResponse>,
        permissions: PermissionsBuilder,
        allowSome: boolean = false
    ): boolean {
        return currentUser ? hasPermissions(currentUser, permissions.build(), allowSome) : false
    }

    static isCMEWideUser(currentUser: Definable<AdminUserResponse>): boolean {
        return currentUser ? isCMEWideUser(currentUser) : false
    }

    static isTelSysWideUser(currentUser: Definable<AdminUserResponse>): Definable<boolean> {
        return isTelSysWideUser(currentUser)
    }

    static hasTelSysType(currentUser: Definable<AdminUserResponse>, telSysType: TelSysTypes): Definable<boolean> {
        if (IsVarEmpty(currentUser)) return undefined
        return hasTelSysType(currentUser, telSysType)
    }
}

export const SELF_PROVISIONING_PERMISSIONS: Permissions[] = [
    Permissions.CME_READ,
    Permissions.ACDAGENT_CREATE,
    Permissions.ACDAGENT_DELETE,
    Permissions.ACDAGENT_READ,
    Permissions.ACDAGENT_UPDATE,
    Permissions.DEVICE_READ,
    Permissions.DEVICE_CREATE,
    Permissions.DEVICE_DELETE,
    Permissions.DEVICE_UPDATE,
    Permissions.PARTITION_CREATE,
    Permissions.PARTITION_DELETE,
    Permissions.PARTITION_READ,
    Permissions.PARTITION_UPDATE,
    Permissions.REPORT_COPY,
    Permissions.SERVER_STATUS,
    Permissions.SIGN_IN_AS,
    Permissions.PARTITION_SYNC,
    Permissions.TELSYS_STATUS,
    Permissions.TEL_SYS_CREATE,
    Permissions.TEL_SYS_DELETE,
    Permissions.TEL_SYS_READ,
    Permissions.TEL_SYS_UPDATE,
    Permissions.USER_CREATE,
    Permissions.USER_DELETE,
    Permissions.USER_READ,
    Permissions.USER_UPDATE,
    Permissions.DIRECTORY_ENTRY_CREATE,
    Permissions.DIRECTORY_ENTRY_DELETE,
    Permissions.DIRECTORY_ENTRY_READ,
    Permissions.DIRECTORY_ENTRY_UPDATE,
    Permissions.BILLING_CDRs,
    Permissions.BILLING_SUMMARY,
    Permissions.LICENSE_READ,
    Permissions.EPT_ACCESS
]

export const USER_MANAGEMENT_PERMISSIONS: Permissions[] = [Permissions.EPT_ACCESS]

export const QUEUE_MANAGEMENT_PERMISSIONS: Permissions[] = [
    Permissions.DEVICE_READ,
    Permissions.DEVICE_CREATE,
    Permissions.DEVICE_UPDATE,
    Permissions.USER_CREATE,
    Permissions.USER_UPDATE,
    Permissions.USER_READ,
    Permissions.USER_DELETE,
    Permissions.TEL_SYS_READ,
    Permissions.TEL_SYS_UPDATE,
    Permissions.PARTITION_READ,
    Permissions.CME_READ,
    Permissions.DIRECTORY_ENTRY_READ,
    Permissions.DIRECTORY_ENTRY_CREATE,
    Permissions.DIRECTORY_ENTRY_UPDATE,
    Permissions.ACDAGENT_READ,
    Permissions.ACDAGENT_CREATE,
    Permissions.ACDAGENT_UPDATE
]

export const ADMIN_MENU_PERMISSIONS: Permissions[] = [
    Permissions.DEVICE_READ,
    Permissions.ACDAGENT_READ,
    Permissions.DIRECTORY_ENTRY_READ,
    Permissions.PARTITION_READ,
    Permissions.TEL_SYS_READ,
    Permissions.CME_READ,
    Permissions.USER_READ,
    Permissions.RECORDING_READ,
    Permissions.CME_READ,
    Permissions.MESSAGE_READ,
    Permissions.DATABASE_READ
]

export const STATUS_AND_TOOLS_PERMISSIONS: Permissions[] = [
    Permissions.LICENSE_READ,
    Permissions.LOGGING_READ,
    Permissions.LOG_MANAGEMENT_READ,
    Permissions.AUDIT_TRAIL,
    Permissions.TELSYS_STATUS,
    Permissions.OMNICHANNEL_STATUS,
    Permissions.CALL_CONTROL_READ,
    Permissions.SERVER_STATUS
]
